import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

import { WorkerTypesService } from './worker-types.service';
import { WorkerType, WorkerTypesResponse } from '../models/worker-types.model';
import { AppConfigTopNavComponent } from '../../../shared/app-config-nav/app-config-top-nav.component';
import { SearchBarComponent } from '../../../shared/search-bar/search-bar.component';

interface WorkerTypeFiltersVm {
  searchTerm: string;
}

@Component({
  selector: 'app-worker-types',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AppConfigTopNavComponent,
    RouterModule,
    SearchBarComponent,
  ],
  templateUrl: './worker-types.component.html',
  styleUrls: ['./worker-types.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class WorkerTypesComponent implements OnInit, OnDestroy {
  workerTypes$: Observable<WorkerType[]> = of([]);
  isLoading = false;
  errorMessage: string | null = null;

  currentPage = 1;
  totalPages = 1;
  pageSize = 25;
  pageSizes = [25, 50, 100, 250];

  filters: WorkerTypeFiltersVm = {
    searchTerm: '',
  };

  modalOpen = false;
  isEditMode = false;
  currentWorkerType: WorkerType | null = null;
  formModel: Partial<WorkerType> = this.getEmptyWorkerType();

  deleteConfirmOpen = false;
  workerTypeToDelete: WorkerType | null = null;

  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  constructor(
    private readonly workerTypesService: WorkerTypesService,
    private readonly cd: ChangeDetectorRef,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.loadWorkerTypes();
  }

  ngOnDestroy(): void {
    this.unlockBodyScroll();
  }

  private lockBodyScroll(): void {
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = '';
  }

  private syncBodyScrollLock(): void {
    if (this.modalOpen || this.deleteConfirmOpen) {
      this.lockBodyScroll();
      return;
    }

    this.unlockBodyScroll();
  }

  loadWorkerTypes(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.workerTypes$ = this.workerTypesService
      .getWorkerTypes(this.currentPage, this.pageSize, 'name', {
        searchTerm: this.filters.searchTerm,
      })
      .pipe(
        map((res: WorkerTypesResponse | null) => {
          if (!res) {
            this.totalPages = 1;
            return [];
          }

          this.totalPages = res.totalPages ?? 1;
          this.currentPage = res.currentPage ?? 1;
          return res.data ?? [];
        }),
        catchError(() => {
          this.errorMessage = 'Failed to load worker types';
          this.totalPages = 1;
          return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
          this.cd.markForCheck();
        }),
      );
  }

  onSearchTermChange(value: string | null): void {
    this.filters.searchTerm = String(value ?? '').trim();
    this.currentPage = 1;
    this.loadWorkerTypes();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.loadWorkerTypes();
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.loadWorkerTypes();
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.currentWorkerType = null;
    this.formModel = this.getEmptyWorkerType();
    this.modalOpen = true;

    this.syncBodyScrollLock();
  }

  openEditModal(workerType: WorkerType): void {
    this.isEditMode = true;
    this.currentWorkerType = workerType;
    this.formModel = { ...workerType };
    this.modalOpen = true;

    this.syncBodyScrollLock();
  }

  closeModal(): void {
    this.modalOpen = false;
    this.isEditMode = false;
    this.currentWorkerType = null;
    this.formModel = this.getEmptyWorkerType();

    this.syncBodyScrollLock();
  }

  submitWorkerType(form?: NgForm): void {
    if (!this.formModel.name) {
      this.showToast('Name is required', 'error');
      return;
    }

    const payload: Partial<WorkerType> = {
      name: this.formModel.name,
      description: this.formModel.description ?? '',
    };

    const request$ =
      this.isEditMode && this.currentWorkerType
        ? this.workerTypesService.updateWorkerType(this.currentWorkerType.id, payload)
        : this.workerTypesService.createWorkerType(payload);

    request$.subscribe({
      next: () => {
        this.closeModal();

        this.showToast(
          this.isEditMode
            ? 'Worker type updated successfully'
            : 'Worker type created successfully',
          'success',
        );

        this.loadWorkerTypes();
        this.cd.detectChanges();
      },
      error: () => {
        this.showToast(
          this.isEditMode
            ? 'Failed to update worker type'
            : 'Failed to create worker type',
          'error',
        );

        this.cd.detectChanges();
      },
    });
  }

  confirmDelete(workerType: WorkerType): void {
    this.workerTypeToDelete = workerType;
    this.deleteConfirmOpen = true;

    this.syncBodyScrollLock();
  }

  cancelDelete(): void {
    this.deleteConfirmOpen = false;
    this.workerTypeToDelete = null;

    this.syncBodyScrollLock();
  }

  deleteWorkerType(): void {
    if (!this.workerTypeToDelete) return;

    this.workerTypesService.deleteWorkerType(this.workerTypeToDelete.id).subscribe({
      next: () => {
        this.cancelDelete();

        this.showToast(
          'Worker type deleted successfully',
          'success',
        );

        this.loadWorkerTypes();
        this.cd.detectChanges();
      },
      error: () => {
        this.showToast(
          'Failed to delete worker type',
          'error',
        );

        this.cd.detectChanges();
      },
    });
  }

  showToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;

    setTimeout(() => {
      this.toastVisible = false;
      this.cd.markForCheck();
    }, 2500);
  }

  private getEmptyWorkerType(): Partial<WorkerType> {
    return {
      name: '',
      description: '',
    };
  }

  goToImport(): void {
    this.router.navigate(['/settings/application/workertypes/import']);
  }
}